import BillingsCargoTaxiDetailsResponse, {
  BillingsCargoTaxiDetailsResponseData,
  BillingsCargoTaxiDetailsResponseDataBillingContribution,
  BillingsCargoTaxiDetailsResponseDataBillingContributionBaseAmount,
  BillingsCargoTaxiDetailsResponseDataBillingContributionChargeHaltings,
  BillingsCargoTaxiDetailsResponseDataBillingContributionContractCorrectionPercentage,
  BillingsCargoTaxiDetailsResponseDataBillingContributionDiscountBeingLate,
  BillingsCargoTaxiDetailsResponseDataBillingContributionDiscountIncompatibleCar,
  BillingsCargoTaxiDetailsResponseDataBillingContributionDiscountOptimization,
  BillingsCargoTaxiDetailsResponseDataBillingContributionDiscountOther,
  BillingsCargoTaxiDetailsResponseDataBillingContributionType,
  BillingsCargoTaxiDetailsResponseDataBillingNode,
  BillingsCargoTaxiDetailsResponseDataBillingStatus,
  BillingsCargoTaxiDetailsResponseDataCargoOrder,
  BillingsCargoTaxiDetailsResponseDataContract,
  BillingsCargoTaxiDetailsResponseDataContractBillingModel,
  BillingsCargoTaxiDetailsResponseDataContractCargoCompany,
} from "../../../../api/raily/billings/cargo-taxi/details/billings-cargo-taxi-details.response";
import BillingModel from "../../../../types/billing-model";
import BillingStatus from "../../../../types/billing-status";
import BillingsCargoTaxiDetails, {
  BillingsCargoTaxiDetailsBillingContribution,
  BillingsCargoTaxiDetailsBillingContributionBaseAmount,
  BillingsCargoTaxiDetailsBillingContributionChargeHaltings,
  BillingsCargoTaxiDetailsBillingContributionContractCorrectionPercentage,
  BillingsCargoTaxiDetailsBillingContributionDiscountBeingLate,
  BillingsCargoTaxiDetailsBillingContributionDiscountIncompatibleCar,
  BillingsCargoTaxiDetailsBillingContributionDiscountOptimization,
  BillingsCargoTaxiDetailsBillingContributionDiscountOther,
  BillingsCargoTaxiDetailsBillingContributionType,
  BillingsCargoTaxiDetailsBillingNode,
  BillingsCargoTaxiDetailsCargoOrder,
  BillingsCargoTaxiDetailsContract,
  BillingsCargoTaxiDetailsContractCargoCompany,
} from "./billings-cargo-taxi-details";

const createContributionBaseAmount = (
  contribution: BillingsCargoTaxiDetailsResponseDataBillingContributionBaseAmount,
): BillingsCargoTaxiDetailsBillingContributionBaseAmount => {
  return {
    type: BillingsCargoTaxiDetailsBillingContributionType.BASE_AMOUNT,
    comment: contribution.comment,
    distance: contribution.distance,
    rate: contribution.rate,
  };
};

const createContributionChargeHaltings = (
  contribution: BillingsCargoTaxiDetailsResponseDataBillingContributionChargeHaltings,
): BillingsCargoTaxiDetailsBillingContributionChargeHaltings => {
  return {
    type: BillingsCargoTaxiDetailsBillingContributionType.CHARGE_HALTING,
    comment: contribution.comment,
    distance: contribution.distance,
    amount: contribution.amount,
  };
};

const createContributionContractCorrectionPercentage = (
  contribution: BillingsCargoTaxiDetailsResponseDataBillingContributionContractCorrectionPercentage,
): BillingsCargoTaxiDetailsBillingContributionContractCorrectionPercentage => {
  return {
    type: BillingsCargoTaxiDetailsBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE,
    comment: contribution.comment,
    percentage: contribution.percentage,
    amount: contribution.amount,
  };
};

const createContributionDiscountBeingLate = (
  contribution: BillingsCargoTaxiDetailsResponseDataBillingContributionDiscountBeingLate,
): BillingsCargoTaxiDetailsBillingContributionDiscountBeingLate => {
  return {
    type: BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_BEING_LATE,
    comment: contribution.comment,
    amount: contribution.amount,
  };
};

const createContributionDiscountIncompatibleCar = (
  contribution: BillingsCargoTaxiDetailsResponseDataBillingContributionDiscountIncompatibleCar,
): BillingsCargoTaxiDetailsBillingContributionDiscountIncompatibleCar => {
  return {
    type: BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR,
    comment: contribution.comment,
    amount: contribution.amount,
  };
};

const createContributionDiscountOptimization = (
  contribution: BillingsCargoTaxiDetailsResponseDataBillingContributionDiscountOptimization,
): BillingsCargoTaxiDetailsBillingContributionDiscountOptimization => {
  return {
    type: BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OPTIMIZATION,
    comment: contribution.comment,
    amount: contribution.amount,
  };
};

const createContributionDiscountOther = (
  contribution: BillingsCargoTaxiDetailsResponseDataBillingContributionDiscountOther,
): BillingsCargoTaxiDetailsBillingContributionDiscountOther => {
  return {
    type: BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OTHER,
    comment: contribution.comment,
    amount: contribution.amount,
  };
};

const createContribution = (
  contribution: BillingsCargoTaxiDetailsResponseDataBillingContribution,
): BillingsCargoTaxiDetailsBillingContribution => {
  switch (contribution.type) {
    case BillingsCargoTaxiDetailsResponseDataBillingContributionType.BASE_AMOUNT:
      return createContributionBaseAmount(contribution);
    case BillingsCargoTaxiDetailsResponseDataBillingContributionType.CHARGE_HALTING:
      return createContributionChargeHaltings(contribution);
    case BillingsCargoTaxiDetailsResponseDataBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE:
      return createContributionContractCorrectionPercentage(contribution);
    case BillingsCargoTaxiDetailsResponseDataBillingContributionType.DISCOUNT_BEING_LATE:
      return createContributionDiscountBeingLate(contribution);
    case BillingsCargoTaxiDetailsResponseDataBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR:
      return createContributionDiscountIncompatibleCar(contribution);
    case BillingsCargoTaxiDetailsResponseDataBillingContributionType.DISCOUNT_OPTIMIZATION:
      return createContributionDiscountOptimization(contribution);
    case BillingsCargoTaxiDetailsResponseDataBillingContributionType.DISCOUNT_OTHER:
      return createContributionDiscountOther(contribution);
  }
};

const createContributions = (
  contributions: BillingsCargoTaxiDetailsResponseDataBillingContribution[],
): BillingsCargoTaxiDetailsBillingContribution[] => {
  return contributions
    .map(createContribution)
    .filter(
      (contribution) => contribution,
    ) as BillingsCargoTaxiDetailsBillingContribution[];
};

const createNode = (
  node: BillingsCargoTaxiDetailsResponseDataBillingNode,
): BillingsCargoTaxiDetailsBillingNode => {
  return {
    allowCharge: node.allowCharge,
    description: node.description,
    distance: node.distance,
    editableCoordinates: node.editableCoordinated,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    lat: node.lat,
    lon: node.lon,
    nodeUuid: node.nodeId,
    plannedDistance: node.plannedDistance,
    uuid: node.id,
    checkoutDate: node.checkoutDate,
    checkoutEventUuid: node.checkoutEventId,
    orderUuid: node.orderId,
    plannedDate: node.plannedDate,
  };
};

const createNodes = (
  nodes: BillingsCargoTaxiDetailsResponseDataBillingNode[],
): BillingsCargoTaxiDetailsBillingNode[] => {
  return nodes.map(createNode);
};

const createStatus = (
  status: BillingsCargoTaxiDetailsResponseDataBillingStatus,
): BillingStatus => {
  switch (status) {
    case BillingsCargoTaxiDetailsResponseDataBillingStatus.ACCEPTED:
      return BillingStatus.ACCEPTED;
    case BillingsCargoTaxiDetailsResponseDataBillingStatus.CREATED:
      return BillingStatus.CREATED;
    case BillingsCargoTaxiDetailsResponseDataBillingStatus.REJECTED:
      return BillingStatus.REJECTED;
    case BillingsCargoTaxiDetailsResponseDataBillingStatus.REOPENED:
      return BillingStatus.REOPENED;
    case BillingsCargoTaxiDetailsResponseDataBillingStatus.REOPEN_REQUEST:
      return BillingStatus.REOPEN_REQUEST;
  }
};

const createCargoOrder = (
  cargoOrder: BillingsCargoTaxiDetailsResponseDataCargoOrder,
): BillingsCargoTaxiDetailsCargoOrder => {
  return {
    humanId: cargoOrder.humanId,
    uuid: cargoOrder.id,
  };
};

const createContractModel = (
  model: BillingsCargoTaxiDetailsResponseDataContractBillingModel,
): BillingModel => {
  switch (model) {
    case BillingsCargoTaxiDetailsResponseDataContractBillingModel.AB:
      return BillingModel.AB;
    case BillingsCargoTaxiDetailsResponseDataContractBillingModel.ABA:
      return BillingModel.ABA;
    case BillingsCargoTaxiDetailsResponseDataContractBillingModel.SABS:
      return BillingModel.SABS;
    case BillingsCargoTaxiDetailsResponseDataContractBillingModel.OTHER:
      return BillingModel.OTHER;
  }
};

const createContractCargoCompany = (
  cargoCompany: BillingsCargoTaxiDetailsResponseDataContractCargoCompany,
): BillingsCargoTaxiDetailsContractCargoCompany => {
  return {
    companyName: cargoCompany.companyName,
    displayName: cargoCompany.displayName,
  };
};

const createContract = (
  contract: BillingsCargoTaxiDetailsResponseDataContract,
): BillingsCargoTaxiDetailsContract => {
  return {
    allowChargeDuringRide: contract.allowChargeDuringRide,
    allowChargeWhileApproaching: contract.allowChargeWhileApproaching,
    allowChargeWhileReturning: contract.allowChargeWhileReturning,
    discountLimit: contract.discountLimit,
    distanceRate: contract.distanceRate,
    haltingTimeAfterMinutes: contract.haltingTimeAfterMinutes,
    haltingTimeRate: contract.haltingTimeRate,
    model: createContractModel(contract.model),
    cargoCompany: createContractCargoCompany(contract.cargoCompany),
  };
};

const createData = (
  data: BillingsCargoTaxiDetailsResponseData,
): BillingsCargoTaxiDetails => {
  return {
    allContributionsAmount: data.allContributionsAmount,
    amountForChargeHaltings: data.amountForChargeHaltings,
    amountForChargeHighways: data.amountForChargeHighways,
    amountForDistance: data.amountForDistance,
    baseAmountValue: data.baseAmountValue,
    billingContributions: createContributions(data.billingContributions),
    billingNodes: createNodes(data.billingNodes),
    contractCorrectionPercentage: data.contractCorrectionPercentage,
    date: data.date,
    distance: data.distance,
    passengers: data.passengers,
    status: createStatus(data.status),
    sumOfDiscounts: data.sumOfDiscounts,
    cargoOrder: createCargoOrder(data.cargoOrder),
    contract: createContract(data.contract),
    planEntryUuid: data.planEntryId,
  };
};

const create = (
  response: BillingsCargoTaxiDetailsResponse,
): BillingsCargoTaxiDetails => {
  return createData(response.data);
};

const billingsCargoTaxiDetailsFactory = { create };

export default billingsCargoTaxiDetailsFactory;
